import { getSupabase } from 'shared/supabase/client';
import { logActivity } from 'shared/lib/activityLog';
import { mapDbBatch } from 'shared/data/mappers';
import type { Batch } from 'types';

function unwrapOne<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

function isPastEnrollment(
  enrollmentStatus: string,
  batchStatus: Batch['status']
): boolean {
  return (
    enrollmentStatus !== 'active' || batchStatus === 'completed' || batchStatus === 'cancelled'
  );
}

export interface AcceptInviteResult {
  ok: boolean;
  error?: string;
  expected_email?: string;
  invite_type?: string;
  institution_name?: string;
  batch_id?: string;
  batch_name?: string;
  sadhak_code?: string;
  redirect_hint?: string;
}

export async function acceptInstitutionInvite(token: string): Promise<AcceptInviteResult> {
  const { data, error } = await getSupabase().rpc('inst_accept_invite', {
    p_token: token,
  });
  if (error) {
    const message = error.message.includes('seat_limit_reached')
      ? 'This institute has reached its sadhak seat limit. Ask the institute to upgrade their license or free a seat before accepting.'
      : error.message;
    throw new Error(message);
  }
  const result = data as AcceptInviteResult;
  if (result.ok) {
    logActivity({
      action: 'enrollment.accepted',
      summary: result.batch_name
        ? `Joined batch: ${result.batch_name}`
        : `Accepted invite${result.institution_name ? ` at ${result.institution_name}` : ''}`,
      app: 'ai',
      resourceType: result.batch_id ? 'batch' : 'invite',
      resourceId: result.batch_id,
      metadata: {
        inviteType: result.invite_type,
        institutionName: result.institution_name,
        batchName: result.batch_name,
        sadhakCode: result.sadhak_code,
      },
    });
  }
  return result;
}

export async function fetchMyEnrolledBatches(): Promise<Batch[]> {
  const supabase = getSupabase();
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return [];

  const { data: profile } = await supabase
    .from('core_users')
    .select('id')
    .eq('auth_user_id', session.session.user.id)
    .is('deleted_at', null)
    .maybeSingle();

  if (!profile) return [];

  const { data, error } = await supabase
    .from('inst_batch_enrollments')
    .select(`
      id,
      status,
      institution_id,
      inst_institutions ( id, name ),
      inst_batches!inner (
        *,
        core_schools!inner ( slug ),
        teacher:core_users!teacher_id ( first_name, last_name ),
        core_batch_topics ( name_en, is_other )
      )
    `)
    .eq('user_id', profile.id)
    .is('deleted_at', null)
    .in('status', ['active', 'inactive']);

  if (error) throw error;

  const batches: Batch[] = [];

  for (const row of data ?? []) {
    const batchRow = unwrapOne(
      row.inst_batches as Record<string, unknown> | Record<string, unknown>[] | null
    );
    if (!batchRow) continue;

    const institution =
      unwrapOne(
        row.inst_institutions as { id: string; name: string } | { id: string; name: string }[] | null
      ) ?? null;
    const enrollmentStatus = row.status as 'active' | 'inactive';
    const mapped = mapDbBatch(batchRow);

    batches.push({
      ...mapped,
      institutionId: (row.institution_id as string) ?? institution?.id,
      institutionName: institution?.name ?? 'Institute',
      enrollmentId: row.id as string,
      enrollmentStatus,
      isPastEnrollment: isPastEnrollment(enrollmentStatus, mapped.status),
    });
  }

  return enrichBatchesWithCardStats(batches);
}

type BatchCardStatsRow = {
  batch_id: string;
  student_count: number;
  due_assignment_count: number;
  overdue_assignment_count: number;
  next_due_at: string | null;
};

/** DEFINER RPC - classmate counts + due assignments (RLS cannot expose peer enrollments). */
async function enrichBatchesWithCardStats(batches: Batch[]): Promise<Batch[]> {
  if (batches.length === 0) return batches;

  const currentIds = batches
    .filter((b) => !b.isPastEnrollment)
    .map((b) => b.id);

  // Past-only list: still try classmate counts for history cards; due stats less critical.
  const ids = currentIds.length > 0 ? currentIds : batches.map((b) => b.id);

  try {
    const { data, error } = await getSupabase().rpc('stud_my_batch_card_stats', {
      p_batch_ids: ids,
    });
    if (error) throw error;

    const payload = data as {
      ok?: boolean;
      batches?: BatchCardStatsRow[];
    };
    if (!payload?.ok || !Array.isArray(payload.batches)) return batches;

    const byId = new Map(payload.batches.map((row) => [row.batch_id, row]));
    return batches.map((batch) => {
      const stats = byId.get(batch.id);
      if (!stats) return batch;
      return {
        ...batch,
        studentCount: stats.student_count,
        dueAssignmentCount: stats.due_assignment_count,
        overdueAssignmentCount: stats.overdue_assignment_count,
        nextDueAt: stats.next_due_at,
      };
    });
  } catch {
    // Migration 139 not applied yet, or transient RPC failure - keep base rows.
    return batches;
  }
}
